import { useEffect, useState } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PRODUCT_TRACKING_CATEGORY_TREND_LIMIT } from "../../constant/productTracking/productTrackingConfig";
import DateRangeFields from "./DateRangeFields";

const PRIMARY = "#6759FF";
const LINE_COLORS = ["#6759FF", "#F59E0B", "#10B981", "#EF4444", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6"];
const CATEGORY_OPTIONS_LIMIT = 100;

// One metric per day, one line per category (leafCategory). Starts with the top categories by
// products in the range; the user can pick others. `baseFilterItems` are the page's filters; `metrics`
// are the marketplace's trend metrics.
const CategoryTrendDialog = ({ open, onClose, metrics, dateRange, baseFilterItems, postQuery, formatValue }) => {
  const [range, setRange] = useState(dateRange);
  const [metric, setMetric] = useState(metrics[0].key);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [error, setError] = useState("");
  const metricConfig = metrics.find((m) => m.key === metric);

  useEffect(() => {
    if (open) setRange(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtersWith = (extra) => {
    const and = [...baseFilterItems, ...extra];
    return and.length ? { dimensions: { type: "AND_RULE", and } } : undefined;
  };

  // Categories in the range, most products first; the top ones are pre-selected.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setIsLoadingOptions(true);
    postQuery(
      {
        dimensions: ["leafCategory"],
        measures: ["productCount"],
        timeDimensions: [{ dimension: "date", dateRange: range }],
        filters: filtersWith([]),
        order: [["productCount", "desc"]],
        limit: CATEGORY_OPTIONS_LIMIT,
      },
      controller.signal
    )
      .then((data) => {
        const names = data.map((r) => r.leafCategory).filter(Boolean);
        setOptions(names);
        setSelected((prev) => {
          const kept = prev.filter((c) => names.includes(c));
          return kept.length ? kept : names.slice(0, PRODUCT_TRACKING_CATEGORY_TREND_LIMIT);
        });
        setIsLoadingOptions(false);
      })
      .catch((err) => {
        if (axios.isCancel?.(err)) return;
        setError(err?.response?.data?.message || "Could not load categories");
        setIsLoadingOptions(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, range?.[0], range?.[1], baseFilterItems]);

  useEffect(() => {
    if (!open || !selected.length) {
      setRows([]);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError("");
    postQuery(
      {
        dimensions: ["date", "leafCategory"],
        measures: [metric],
        timeDimensions: [{ dimension: "date", dateRange: range }],
        filters: filtersWith([{ type: "RULE_ITEM", member: "leafCategory", operator: "equals", values: selected }]),
        order: [["date", "asc"]],
      },
      controller.signal
    )
      .then((data) => {
        setRows(data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel?.(err)) return;
        setRows([]);
        setError(err?.response?.data?.message || "Could not load trend");
        setIsLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, metric, selected.join("|"), range?.[0], range?.[1], baseFilterItems]);

  const labels = [...new Set(rows.map((r) => r.date))].sort();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Category trends</Typography>
        <Typography sx={{ fontSize: "12px", color: "#8C9196" }}>
          {metricConfig?.label} by day, one line per category
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2, mt: 1 }}>
          <DateRangeFields value={range} onChange={setRange} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Metric</InputLabel>
            <Select label="Metric" value={metric} onChange={(e) => setMetric(e.target.value)} sx={{ fontSize: "12px" }}>
              {metrics.map((m) => (
                <MenuItem key={m.key} value={m.key} sx={{ fontSize: "12px" }}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={options}
            loading={isLoadingOptions}
            loadingText="Loading categories..."
            value={selected}
            onChange={(e, value) => value.length <= LINE_COLORS.length && setSelected(value)}
            getOptionDisabled={(option) => !selected.includes(option) && selected.length >= LINE_COLORS.length}
            renderOption={(props, option, { selected: checked }) => (
              <li {...props} style={{ fontSize: "12px" }}>
                <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 1 }} />
                {option}
              </li>
            )}
            renderTags={(value) => (
              <Typography sx={{ fontSize: "12px", px: 1 }}>{value.length} categories</Typography>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categories"
                placeholder="Search category"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingOptions && <CircularProgress size={16} sx={{ color: PRIMARY }} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: 260, flexGrow: 1, "& .MuiInputBase-root": { fontSize: "12px" } }}
          />
        </Box>
        {error && (
          <Typography color="error" sx={{ fontSize: "13px", mb: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ height: 380, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {/* The trend waits for the category list on open, so both count as loading. */}
          {isLoading || isLoadingOptions ? (
            <CircularProgress size={28} sx={{ color: PRIMARY }} />
          ) : labels.length ? (
            <Line
              data={{
                labels,
                datasets: selected.map((category, i) => ({
                  label: category,
                  data: labels.map((d) => {
                    const r = rows.find((x) => x.date === d && x.leafCategory === category);
                    return r && r[metric] !== null && r[metric] !== undefined ? Number(r[metric]) : null;
                  }),
                  borderColor: LINE_COLORS[i % LINE_COLORS.length],
                  backgroundColor: LINE_COLORS[i % LINE_COLORS.length],
                  tension: 0.3,
                  spanGaps: true,
                })),
              }}
              options={{
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                  legend: { position: "bottom" },
                  datalabels: { display: false },
                  tooltip: {
                    callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatValue(ctx.raw, metricConfig?.format)}` },
                  },
                },
                scales: { y: { beginAtZero: false, ticks: { callback: (v) => formatValue(v, metricConfig?.format) } } },
              }}
            />
          ) : (
            <Typography sx={{ color: "#8C9196", fontSize: "13px" }}>No scraped data for this date range.</Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryTrendDialog;
